import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { prductData } from "../Data";

const SingleProduct = ({ openDemo }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = prductData?.find((item) => item.id == id);
  const subpointsArray = product?.subpoints?.split(/\d+\./)?.filter(Boolean);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white pt-[82px]">
        <div className="text-center px-6">
          <h2 className="text-3xl font-teko text-slate-900 mb-4">Product Not Found</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 font-bold uppercase tracking-widest text-[10px] border-b border-blue-600 pb-1"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-[82px] pb-12 px-5 md:px-6">
      <div className="max-w-6xl mx-auto">

        {/* nav back */}
        <div className="py-4 md:py-8" data-aos="fade-in">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-[10px] font-bold uppercase tracking-[3px] text-slate-400 hover:text-blue-600 transition-colors"
          >
            <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Products Section
          </button>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
          <div className="w-full lg:col-span-5 lg:sticky lg:top-28" data-aos="fade-right">
            <div className="relative group bg-slate-50/50 rounded-2xl md:rounded-3xl p-5 md:p-8 aspect-square flex items-center justify-center border border-slate-100 overflow-hidden shadow-sm">
              <div className="absolute inset-0 bg-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <img
                className="relative z-10 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                src={product?.marketingImage || product?.image}
                alt={product?.title}
              />
            </div>
            <button
              onClick={() => openDemo(product?.title)}
              className="w-full mt-4 py-3.5 md:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl md:rounded-2xl font-bold uppercase tracking-widest text-xs transition-all shadow-lg shadow-blue-100 active:scale-[0.98]"
            >
              Request a Demo
            </button>
          </div>


          <div className="w-full lg:col-span-7" data-aos="fade-up">
            <div className="mb-5 md:mb-6">
              <div className="inline-block px-3 py-1 bg-blue-50 rounded-lg mb-3">
                <span className="text-blue-600 font-bold text-[9px] uppercase tracking-[3px]">
                  Product Overview
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-teko uppercase text-slate-900 leading-tight tracking-wide">
                {product?.title}
              </h1>
              {product?.headline && (
                <p className="mt-2 text-sm font-semibold text-blue-600">{product.headline}</p>
              )}
            </div>

            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[2px] text-slate-400 mb-2">
                  The Solution
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed text-justify whitespace-pre-wrap">
                  {product?.description}
                </p>
              </div>

              {subpointsArray && subpointsArray.length > 0 && (
                <div className="pt-4 md:pt-6 border-t border-slate-100 space-y-6">
                  {(() => {
                    const firstColonIndex = subpointsArray[0]?.indexOf(":");
                    const hasSections = firstColonIndex !== -1 && firstColonIndex < 40;

                    if (!hasSections) {
                      return (
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 mb-2 border-b border-slate-100 pb-1">
                            Key Features
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4">
                            {subpointsArray.map((point, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold text-[11px] md:text-xs mt-0.5">
                                  {index + 1}.
                                </span>
                                <p className="text-slate-600 text-[11px] md:text-xs leading-relaxed">
                                  {point?.trim()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return subpointsArray.map((point, index) => {
                      const trimmed = point.trim();
                      const colonIndex = trimmed.indexOf(":");
                      let title = "Features";
                      let content = trimmed;

                      if (colonIndex !== -1 && colonIndex < 40) {
                        title = trimmed.substring(0, colonIndex).trim();
                        content = trimmed.substring(colonIndex + 1).trim();
                      }

                      const items = content.split("|").map((i) => i.trim()).filter(Boolean);

                      return (
                        <div key={index}>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 mb-2 border-b border-slate-100 pb-1">
                            {title}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1.5 gap-x-4">
                            {items.map((item, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                                <p className="text-slate-600 text-[11px] md:text-xs leading-relaxed">
                                  {item}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 border-t border-slate-100 pt-10">
          <h3 className="text-xl md:text-2xl font-teko uppercase text-slate-900 tracking-wide mb-6">
            Other Products
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {prductData
              .filter((p) => p.id != id)
              .slice(0, 4)
              .map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group flex flex-col items-center p-3 md:p-4 bg-slate-50 border border-slate-100 rounded-xl hover:bg-white hover:shadow-md hover:border-blue-200 transition-all duration-300 text-center"
                >
                  <div className="h-14 md:h-20 w-full flex items-center justify-center mb-2 md:mb-3">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <p className="text-[10px] md:text-xs font-bold text-slate-700 uppercase tracking-wide leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {p.title}
                  </p>
                </Link>
              ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SingleProduct;
